import { useEffect, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { useGLTF } from "@react-three/drei";

import { usePrefersReducedMotion } from "../../../hooks/usePrefersReducedMotion.js";
import { applyCarMaterials } from "./applyCarMaterials.js";
import { measureCar } from "./carGeometry.js";
import { PARALLAX_TILT_X, PARALLAX_TILT_Z, REDUCED_MOTION_PROGRESS, carYawAt } from "./carStageConfig.js";

const CAR_MODEL_URL = "/models/hero-car.glb";

useGLTF.preload(CAR_MODEL_URL);

/**
 * The car, turning through a full 180° reveal (front → side → rear) driven
 * by `progress` (a framer-motion MotionValue, 0–1). A pointer-parallax tilt
 * layers on top for a subtle interactive feel. When reduced motion is
 * preferred, rotation freezes at a fixed, flattering three-quarter angle
 * instead of tracking scroll.
 *
 * Geometry comes from `hatchback.glb` untouched; `carGeometry` normalises it
 * onto the metre scale, origin, and ground plane the camera rig and studio
 * lighting are built around, and the reveal's yaw pivots about the car's own
 * centre rather than the model file's arbitrary origin. Material values are
 * retuned by name in `carMaterials.js` and applied in `applyCarMaterials.js`;
 * the `.glb` itself is never modified.
 *
 * A blurred contact shadow (see `GroundShadow` in `CarStage.jsx`) grounds
 * the car against the page's background photo rather than it floating —
 * this component itself still knows nothing about a literal ground plane
 * mesh, just the y=0 the car's own geometry stands on.
 */
export function CarModel({ progress }) {
  const groupRef = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();
  const { scene } = useGLTF(CAR_MODEL_URL);
  const geometry = measureCar(scene);

  useEffect(() => {
    applyCarMaterials(scene);
  }, [scene]);

  useFrame((state) => {
    const group = groupRef.current;
    if (!group) return;

    group.rotation.y = carYawAt(prefersReducedMotion ? REDUCED_MOTION_PROGRESS : progress.get());

    const targetX = prefersReducedMotion ? 0 : state.pointer.y * PARALLAX_TILT_X;
    const targetZ = prefersReducedMotion ? 0 : -state.pointer.x * PARALLAX_TILT_Z;
    group.rotation.x += (targetX - group.rotation.x) * 0.06;
    group.rotation.z += (targetZ - group.rotation.z) * 0.06;
  });

  return (
    <group ref={groupRef}>
      <group scale={geometry.scale} position={geometry.offset}>
        <primitive object={scene} />
      </group>
    </group>
  );
}
